<template>
  <div class="flex flex-col flex-1">
    <div class="flex space-x-4 py-4 justify-center">
      <div>
        <p class="m-0 text-sm mb-1 opacity-50">Даты</p>
        <DatePicker
          v-model="period.from"
          type="date"
          format="DD.MM.YYYY"
          placeholder="с"
          :clearable="false"
          :disabled-date="disabledFromPeriod(period)"
        />
      </div>

      <div>
        <p class="m-0 text-sm mb-1 opacity-0">Даты</p>
        <DatePicker
          v-model="period.to"
          type="date"
          format="DD.MM.YYYY"
          placeholder="по"
          :clearable="false"
          :disabled-date="disabledToPeriod(period)"
        />
      </div>

      <div class="w-75">
        <uiDropdown
          v-model="assignedTo"
          label="Сотрудник"
          :options="supports"
          :reduce="item => item.value"
          clearSearchOnSelect
          class="flex-1"
        />
      </div>
    </div>

    <div class="flex flex-1 justify-center overflow-hidden">
      <div class="tickets">
        <div
          class="ticket"
          :class="{ selected: selected && selected.id === t.id }"
          v-for="t in tickets"
          :key="t.id"
          @click="select(t)"
        >
          <div class="row">
            <div class="name" v-if="t.client">
              {{ t.client.name }} {{ t.client.surname }}
              <span>&#8594; {{ getUser(t.assignedTo) }}</span>
            </div>
            <div class="name" v-else>—</div>
          </div>
          <div class="row">
            <div class="block info" style="width: 70px">
              {{ t.startDate }}
            </div>
            <div class="block info results">
              {{ t.startTime }}
              &#8594;
              <span :class="{ failed: t.assignedTime > 15 }">{{
                t.assignedTime + ' мин'
              }}</span>
              &#8594; В работе
              <template v-if="t.closed">
                &#8594;
                <span>{{ t.closedTime + ' мин' }}</span>
                &#8594; Закрыт в {{ t.endTime }}
              </template>
            </div>
          </div>
        </div>

        <div class="flex pt-5 items-center">
          <uiButton
            v-if="!noMore"
            color="light"
            class="w-50"
            @click="loadTickets"
          >
            Загрузить ещё
          </uiButton>

          <p class="ml-auto m-0 text-sm opacity-50">
            {{ totalCountText }}
          </p>
        </div>

        <div class="dumb" />
      </div>
      <div class="details">
        <template v-if="selected">
          <div class="client" style="margin-bottom: 5px">
            <img
              class="icon"
              :src="require(`@/assets/emoji/${selected.icon}.png`)"
            />
            {{ selected.client.name }}
            <br />
            {{ selected.client.surname }}
          </div>

          <div class="global-id">ticket {{ selected.id }}</div>
          <div class="global-id">chat {{ selected.chat }}</div>
          <div class="global-id">client {{ selected.client.id }}</div>

          <uiButton
            color="light"
            style="width: 200px; margin-top: 10px"
            @click="openChat"
          >
            Открыть чат
          </uiButton>

          <div class="text-sm history">
            <div
              class="grid grid-cols-[70px,180px,120px,1fr]"
              v-for="(h, i) in history"
              :key="i"
            >
              <span>{{ h.at }}</span>
              <span>{{ h.title }}</span>
              <div>
                <span v-if="h.to">
                  {{ getUser(h.by) }} &#8594; {{ getUser(h.to) }}
                </span>
                <span v-else>{{ getUser(h.by) }}</span>
              </div>

              <div class="text-xs">
                <template v-if="h.event === 'updatedMessage'">
                  <div
                    v-html="getDiff(h.details.oldText, h.details.newText)"
                    class="diff-text"
                  />
                </template>

                <template v-if="h.event === 'deletedMessage'">
                  <p
                    v-if="h.details.kind === 'text'"
                    class="bg-red-200 m-0 table"
                  >
                    {{ h.details.text }}
                  </p>
                  <p v-if="h.details.kind === 'photo'" class="m-0">
                    <uiImage :url="h.details.attachment.url" w="70" />
                  </p>
                </template>
              </div>

              <div
                v-if="i < history.length - 1"
                class="text-xs opacity-50 col-span-full py-1 pl-24"
              >
                &#8595; <span>{{ h.diff }}</span>
              </div>
            </div>
          </div>

          <uiButton
            style="width: 200px"
            v-if="!messages.length"
            @click="loadMessages"
          >
            Покеж переписку
          </uiButton>
          <div class="messages" v-else>
            <div
              class="message-row"
              :class="{ user: isClient(m.from) }"
              v-for="m in messages"
              :key="m.id"
            >
              <div class="message">
                {{ m.text }}
              </div>
              <div class="info">
                {{ messageInfo(m.from, m.fromName, m.createdAt) }}
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { API } from '@/config'
import {
  format,
  formatDistanceStrict,
  differenceInMinutes,
  isToday,
  startOfDay,
  endOfDay
} from 'date-fns'
import { ru } from 'date-fns/locale'
import DatePicker from 'vue2-datepicker'
import { getFullname } from '@/utils/name'
import { pluralize } from 'numeralize-ru'
import Diff from 'text-diff'
import { disabledFromPeriod, disabledToPeriod } from '@/utils/datepicker'

const YEAR = new Date().getFullYear()

export default {
  name: 'AnalyticsPage',
  components: {
    DatePicker
  },
  data () {
    return {
      loading: false,
      noMore: false,
      rawTickets: [],
      selected: null,
      messages: [],
      skip: 0,
      period: {
        from: null,
        to: null
      },
      assignedTo: null,
      supports: []
    }
  },
  computed: {
    admins () {
      return this.$store.getters.admins
    },
    tickets () {
      return this.rawTickets
        .filter(t => t.assigned)
        .map(t => {
          let closedTime
          let assignedTime = differenceInMinutes(
            new Date(t.assignedAt),
            new Date(t.createdAt)
          )

          if (t.closed) {
            closedTime = differenceInMinutes(
              new Date(t.closedAt),
              new Date(t.assignedAt)
            )
          }

          return {
            assignedTime: assignedTime,
            closedTime: closedTime,
            startDate: this.formatDate(t.createdAt, 'dd.MM.yyyy'),
            startTime: this.formatDate(t.createdAt, 'HH:mm'),
            endTime: this.formatDate(t.closedAt, 'HH:mm'),
            ...t
          }
        })
    },
    history () {
      if (!this.selected) return
      let events = {
        opened: 'Открыт',
        assigned: 'Назначен',
        merged: 'Объединён',
        closed: 'Закрыт',
        updatedMessage: 'Изменено сообщение',
        deletedMessage: 'Удалено сообщение'
      }

      return this.selected.history.map((h, i, arr) => {
        let diff
        if (arr[i + 1]) {
          diff = this.diffInMinutes(arr[i + 1].createdAt, arr[i].createdAt)
          if (diff < 60) {
            diff += ' мин'
          } else {
            diff = this.distance(arr[i + 1].createdAt, arr[i].createdAt)
          }
        }

        let to
        if (h.event === 'assigned' && h.details.assignedTo !== h.createdBy) {
          to = h.details.assignedTo
        }

        let title = events[h.event]
        if (h.event === 'changeLine') {
          let line = h.details.newLine
          title = 'Смена линии'
          if (line === 'first') title = 'В Лапку'
          if (line === 'bk') title = 'В БК'
          if (line === 'dogology') title = 'В Догологию'
        }

        return {
          event: h.event,
          title: title,
          at: this.formatDate(h.createdAt, 'HH:mm'),
          by: h.createdBy,
          to: to,
          details: h.details,
          diff: diff
        }
      })
    },
    totalCountText () {
      const tickets = this.rawTickets
      return [
        pluralize(tickets.length, 'Загружен', 'Загружено', 'Загружено'),
        tickets.length,
        pluralize(tickets.length, 'тикет', 'тикета', 'тикетов')
      ].join(' ')
    }
  },
  created () {
    this.loadSupports()
    this.loadTickets()
  },
  watch: {
    async 'period.from' () {
      await this.loadTickets({ resetSkip: true })
    },
    async 'period.to' () {
      await this.loadTickets({ resetSkip: true })
    },
    async assignedTo () {
      await this.loadTickets({ resetSkip: true })
    }
  },
  methods: {
    async loadSupports () {
      const { data } = await this.$http.get(API.USERS.supports)
      this.supports = data.map(user => ({
        value: user.id,
        label: getFullname(user)
      }))
    },
    async loadTickets ({ resetSkip = false } = {}) {
      if (this.loading) return
      this.loading = true

      if (resetSkip) {
        this.skip = 0
        this.rawTickets = []
        this.noMore = false
        this.selected = null
      }

      const { data } = await this.$http.post(API.TICKETS.history, {
        skip: this.skip,
        from: startOfDay(this.period.from) || null,
        to: endOfDay(this.period.to) || null,
        assignedTo: this.assignedTo
      })
      this.loading = false
      if (!data || data.err) return this.$notify(data.err)

      if (data?.length < 30) {
        this.noMore = true
      } else {
        this.skip += 30
      }

      if (resetSkip) {
        this.rawTickets = data
      } else {
        this.rawTickets.push(...data)
      }
    },
    async loadMessages () {
      if (this.loading) return
      this.loading = true

      let { data } = await this.$http.post(API.TICKETS.messages, {
        ticket: this.selected.id
      })
      this.loading = false
      if (!data || data.err) return this.$notify(data.err)
      this.messages = data
    },
    select (t) {
      this.selected = t
      this.messages = []
    },
    openChat () {
      this.$router.push('/chats/' + this.selected.chat)
    },
    formatDate (d, form) {
      if (!d) return
      return format(new Date(d), form)
    },
    distance (d1, d2) {
      return formatDistanceStrict(new Date(d1), new Date(d2), {
        locale: ru
      })
    },
    diffInMinutes (d1, d2) {
      return differenceInMinutes(new Date(d1), new Date(d2))
    },
    getUser (id) {
      if (this.selected) {
        if (id === this.selected.client.id) {
          return `${this.selected.client.name} ${
            this.selected.client.surname || ''
          }`
        }
      }
      let u = this.admins.find(u => u.id === id)
      if (!u) return 'Никого'
      return `${u.name} ${u.surname}`
    },
    messageInfo (from, name, date) {
      if (!date) return 'Отправляем'

      let form = 'HH:mm dd.MM'
      let d = new Date(date)
      if (isToday(d)) form = 'HH:mm'
      else if (d.getFullYear() !== YEAR) form += '.yyyy'

      return name + ' в ' + format(new Date(date), form)
    },
    isClient (id) {
      return id === this.selected.client.id
    },
    getDiff (oldText, newText) {
      const diff = new Diff()
      const diffArray = diff.main(oldText, newText)
      diff.cleanupEfficiency(diffArray)
      const html = diff.prettyHtml(diffArray)

      return html
    },
    disabledFromPeriod,
    disabledToPeriod
  }
}
</script>

<style lang="postcss">
.diff-text ins {
  @apply bg-green-200 no-underline;
}
.diff-text del {
  @apply bg-red-200 no-underline;
}
</style>

<style lang="less" scoped>
@import '../assets/app.less';

.tickets {
  width: 500px;
  padding: 30px;
  overflow: hidden;
  overflow-y: auto;

  .ticket {
    font-size: 14px;
    margin-bottom: 10px;
    padding: 10px;
    background: @light;
    border-radius: 16px;
    cursor: pointer;

    .row {
      display: flex;
    }

    &.selected {
      background: #fff;
      box-shadow: 0px 5px 20px rgba(0, 0, 0, 0.1);
    }

    .icon {
      width: 40px;
      height: 40px;
      background: green;

      img {
        width: 100%;
      }
    }

    .block {
      font-size: 12px;
    }

    .name {
      padding-bottom: 3px;
      span {
        opacity: 0.5;
      }
    }

    .info {
      color: fade(@dark; 70%);
    }

    .failed {
      color: @red;
      font-weight: bold;
    }
  }
}

.details {
  width: 700px;
  padding: 30px 0;
  margin-left: 20px;
  overflow: hidden;
  overflow-y: auto;

  .client {
    font-size: 20px;
    font-weight: bold;
    display: flex;
    align-items: center;

    .icon {
      height: 40px;
      margin-right: 10px;
    }
  }

  .history {
    margin: 20px 0;
  }
}

.messages {
  padding: 20px;

  .message-row {
    margin-bottom: 10px;

    .message {
      padding: 8px 14px;
      background: @grey;
      width: fit-content;
      max-width: 80%;
      border-radius: 16px;
      font-size: 15px;
    }

    .info {
      font-size: 12px;
      opacity: 0.3;
      margin-top: 3px;
    }

    &.user {
      .message {
        background: @accent;
        color: #fff;
        margin-left: auto;
      }

      .info {
        margin-left: auto;
        text-align: right;
      }
    }

    &.support {
      .message {
        background: @yellow;
        color: #000;
        margin-left: auto;
      }

      .info {
        margin-left: auto;
        text-align: right;
      }
    }

    .ticket-start {
      text-align: center;
      font-size: 13px;
      opacity: 0.3;
      margin: 10px 0;
    }
  }
}
</style>
